import { z } from "zod";

import { coachPlanSchema } from "./coach-plan";

export * from "./coach-plan";

export const defaultLayout = [
  [
    [
      "summary",
      "education",
      "experience",
      "internship",
      "projects",
      "certifications",
      "extracurriculars",
      "languages",
    ],
    ["skills", "profiles", "interests", "volunteer", "references", "awards", "publications"],
  ],
];

// Special layout for LFTS templates where "education" should appear
// at the end of the second column array.
export const lftsLayout = [
  [
    [
      "summary",
      "experience",
      "internship",
      "projects",
      "certifications",
      "extracurriculars",
      "languages",
    ],
    [
      "skills",
      "profiles",
      "interests",
      "volunteer",
      "references",
      "awards",
      "publications",
      "education",
    ],
  ],
];

// Schema
export const metadataSchema = z.object({
  template: z.string().default("rhyhorn"),
  layout: z.array(z.array(z.array(z.string()))).default(defaultLayout), // pages -> columns -> sections
  css: z.object({
    value: z.string().default(".section {\n\toutline: 1px solid #000;\n\toutline-offset: 4px;\n}"),
    visible: z.boolean().default(false),
  }),
  page: z.object({
    margin: z.number().default(18),
    format: z.enum(["a4", "letter"]).default("a4"),
    options: z.object({
      breakLine: z.boolean().default(true),
      pageNumbers: z.boolean().default(true),
    }),
  }),
  theme: z.object({
    background: z.string().default("#ffffff"),
    text: z.string().default("#000000"),
    primary: z.string().default("#dc2626"),
  }),
  typography: z.object({
    font: z.object({
      family: z.string().default("IBM Plex Serif"),
      subset: z.string().default("latin"),
      variants: z.array(z.string()).default(["regular"]),
      size: z.number().default(12),
    }),
    lineHeight: z.number().default(1.5),
    hideIcons: z.boolean().default(true),
    underlineLinks: z.boolean().default(true),
  }),
  notes: z.string().default(""),
  /**
   * ISO timestamp of the first time an AI suggestion was ACCEPTED on this résumé, or null.
   *
   * Drives the "AI Optimized" badge on the résumé list. Stored on the document rather than as a
   * column because the fact belongs to the résumé's content — it is the content that was rewritten —
   * and because it then survives Duplicate, which copies `data` and would otherwise silently drop it.
   *
   * ⚠️ IT HAS TO BE DECLARED HERE TO EXIST AT ALL. Zod objects strip unknown keys, and this schema
   * validates both the client's write and the server's `PATCH /resume/:id`, so a marker tucked into
   * `metadata` without a field would be dropped on the way to the database — silently, since stripping
   * is not an error.
   *
   * `nullish` and NOT `.default(null)`: a default makes the key required in the inferred `Metadata`
   * type, which would force every existing fixture and hand-built metadata literal in the repo to
   * declare a field they have nothing to say about. Optional means a résumé written before this field
   * existed parses unchanged and simply reads `undefined`, which is what the badge check already
   * treats as "not optimized".
   *
   * Set on the first ACCEPT, not when an optimization run starts: a student who opened the optimizer
   * and rejected everything has a résumé the AI did not write, and badging it would be a false claim.
   * See use-coach-suggestion-actions.ts.
   */
  aiOptimizedAt: z.string().nullish(),
  // Persist the assessment with the document, across tabs and devices. Invalid legacy
  // snapshots are discarded without preventing the rest of the resume from saving.
  coachPlan: coachPlanSchema.nullish().catch(null),
  /**
   * The job-match score this résumé was optimized to, from a Career Coach (student-side) run.
   *
   * WHY IT LIVES ON THE DOCUMENT. The score was held only in the coach store, which is
   * sessionStorage — so it existed for exactly one tab and one visit. Nothing about a saved
   * résumé recorded which job it was tuned for or what that was worth, which made an optimized
   * résumé indistinguishable from any other the moment the tab closed. Stored beside
   * `aiOptimizedAt` for the same two reasons: the fact belongs to the content that was rewritten,
   * and it survives Duplicate, which copies `data`.
   *
   * ⚠️ DECLARE EVERY FIELD OR IT DOES NOT EXIST — see the note on `aiOptimizedAt`. Zod objects
   * strip unknown keys and this schema validates both the client's write and the server's
   * `PATCH /resume/:id`, so an undeclared key is dropped silently rather than rejected.
   *
   * ⚠️ WRITTEN ONLY FOR A RUN WITH AN LMS JOB BEHIND IT. The dashboard flow scores a résumé
   * against a pasted job description with no student profile in it, on renormalised weights — a
   * résumé-fit number that is not comparable with a job-match score and has no job to name. See
   * buildDashboardCoachSession.
   *
   * `baseline` and `score` are both kept so the pair reads as the change it was ("37 → 46")
   * rather than a bare total, and `nullish` rather than defaulted so a résumé written before this
   * field existed parses unchanged.
   */
  coachScore: z
    .object({
      jobUuid: z.string(),
      jobTitle: z.string().nullish(),
      companyName: z.string().nullish(),
      /** The LMS score this run started from. */
      baseline: z.number(),
      /** The score the panel was showing when the student confirmed. */
      score: z.number(),
      badge: z.string().nullish(),
      acceptedCount: z.number().nullish(),
      totalCount: z.number().nullish(),
      scoredAt: z.string(),
    })
    .nullish(),
});

// Type
export type Metadata = z.infer<typeof metadataSchema>;

// Defaults
export const defaultMetadata: Metadata = {
  template: "mastersunion",
  layout: defaultLayout,
  css: {
    value: ".section {\n\toutline: 1px solid #000;\n\toutline-offset: 4px;\n}",
    visible: false,
  },
  page: {
    margin: 18,
    format: "a4",
    options: {
      breakLine: true,
      pageNumbers: true,
    },
  },
  theme: {
    background: "#ffffff",
    text: "#000000",
    primary: "#000000",
  },
  typography: {
    font: {
      // family: "IBM Plex Serif",
      family: "Times New Roman",
      subset: "latin",
      // variants: ["regular", "italic", "600"],
      variants: ["regular"],
      size: 12,
    },
    lineHeight: 1.5,
    hideIcons: true,
    underlineLinks: true,
  },
  notes: "",
  aiOptimizedAt: null,
  coachScore: null,
};
